import React, { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { PaperPlaneTilt, Sparkle, User } from "@phosphor-icons/react";
import { Header } from "@/components/Clients";

const MODELS = [
  { id: "claude-sonnet-4-5", label: "Claude Sonnet 4.5", tag: "Deep analysis" },
  { id: "gemini-3-flash", label: "Gemini 3 Flash", tag: "Fast responses" },
];

const SUGGESTIONS = [
  "When is the next GSTR-3B due?",
  "Explain Section 44ADA and who qualifies.",
  "What is the TDS rate for professional fees under 194J?",
  "Old vs New tax regime for a ₹18L salary in FY 2025-26?",
];

const Assistant = () => {
  const [model, setModel] = useState("claude-sonnet-4-5");
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, sending]);

  const send = async (text) => {
    const q = (text || input).trim();
    if (!q || sending) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: q }]);
    setSending(true);
    try {
      const r = await api.post("/ai/chat", { message: q, model, session_id: sessionId });
      setSessionId(r.data.session_id);
      setMessages((m) => [...m, { role: "assistant", content: r.data.answer, model: r.data.model }]);
    } catch (err) {
      toast.error(err.response?.data?.detail || "AI failed");
    } finally {
      setSending(false);
    }
  };

  const onKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  const newChat = () => {
    setSessionId(null);
    setMessages([]);
  };

  return (
    <div className="h-[calc(100vh-4rem)] flex flex-col">
      <div className="flex items-end justify-between">
        <div>
          <p className="text-xs tracking-[0.3em] uppercase text-[#5C6B64]">AI Tax Co-pilot</p>
          <h1 className="font-serif-display text-4xl mt-2 text-[#0F2E22]">Ask anything. In plain English.</h1>
        </div>
        <div className="flex items-center gap-3">
          <select
            value={model}
            onChange={(e) => setModel(e.target.value)}
            data-testid="model-selector"
            className="border border-[#E8EAE8] bg-white px-3 py-2 text-sm outline-none focus:border-[#0F2E22]"
          >
            {MODELS.map((m) => <option key={m.id} value={m.id}>{m.label} — {m.tag}</option>)}
          </select>
          <button onClick={newChat} data-testid="new-chat-btn" className="border border-[#E8EAE8] px-3 py-2 text-xs uppercase tracking-widest text-[#3B463F] hover:bg-[#F3F1EC]">New chat</button>
        </div>
      </div>

      <div ref={scrollRef} className="flex-1 mt-6 border border-[#E8EAE8] bg-white overflow-y-auto p-6">
        {messages.length === 0 ? (
          <div className="max-w-2xl">
            <div className="flex items-center gap-2 text-[#0F2E22]">
              <Sparkle size={18} weight="duotone" className="text-[#D4AF37]" />
              <span className="font-serif-display text-2xl">Try one of these to start</span>
            </div>
            <div className="mt-6 grid gap-3">
              {SUGGESTIONS.map((s) => (
                <button
                  key={s}
                  onClick={() => send(s)}
                  data-testid={`suggest-${s.slice(0, 10)}`}
                  className="text-left border border-[#E8EAE8] px-4 py-3 text-sm text-[#3B463F] hover:bg-[#F3F1EC] hover:border-[#0F2E22] transition-colors"
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
        ) : (
          <div className="space-y-6 max-w-3xl">
            {messages.map((m, i) => (
              <div key={i} className={`flex gap-3 ${m.role === "user" ? "justify-end" : ""}`} data-testid={`msg-${i}`}>
                {m.role === "assistant" && (
                  <div className="w-8 h-8 border border-[#D4AF37] flex items-center justify-center bg-[#FBF5E4] text-[#0F2E22]">
                    <Sparkle size={16} weight="fill" />
                  </div>
                )}
                <div className={`max-w-[85%] px-4 py-3 text-sm leading-relaxed whitespace-pre-wrap ${m.role === "user" ? "bg-[#0F2E22] text-white" : "bg-[#F7F5EF] text-[#0F2E22] border border-[#E8EAE8]"}`}>
                  {m.content}
                </div>
                {m.role === "user" && (
                  <div className="w-8 h-8 border border-[#E8EAE8] flex items-center justify-center bg-white text-[#0F2E22]">
                    <User size={16} weight="duotone" />
                  </div>
                )}
              </div>
            ))}
            {sending && (
              <div className="flex gap-3">
                <div className="w-8 h-8 border border-[#D4AF37] flex items-center justify-center bg-[#FBF5E4]">
                  <Sparkle size={16} weight="fill" className="animate-pulse text-[#0F2E22]" />
                </div>
                <div className="text-sm text-[#5C6B64]">Thinking…</div>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="mt-4 border border-[#E8EAE8] bg-white flex items-end p-3">
        <textarea
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={onKey}
          data-testid="chat-input"
          rows={2}
          placeholder={`Ask a tax question — e.g. "Which ITR form for a freelance developer with ₹22L revenue?"`}
          className="flex-1 resize-none outline-none text-sm px-2 py-1"
        />
        <button
          onClick={() => send()}
          disabled={sending || !input.trim()}
          data-testid="chat-send-btn"
          className="brand-gold-bg px-5 py-3 text-xs uppercase tracking-widest font-semibold flex items-center gap-2 disabled:opacity-50"
        >
          Send <PaperPlaneTilt size={14} weight="fill" />
        </button>
      </div>
    </div>
  );
};

export default Assistant;
